// src/components/ConsultationPage.jsx
import React, { useState, useEffect } from 'react';
import { Mail, Phone, MessageCircle, Send, PhoneCall } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import useAuth from '../hooks/useAuth';

// This would normally come from an API endpoint
const defaultContactInfo = {
  phone: '+374 77 029696',
  email: 'info@tts.am',
  telegram: 'https://t.me/37477029696',
  whatsapp: 'https://wa.me/37477029696',
  viber: 'viber://chat?number=+37477029696'
};

const ConsultationPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [contactInfo, setContactInfo] = useState(defaultContactInfo);
  const [isEditing, setIsEditing] = useState(false);
  const [editedInfo, setEditedInfo] = useState(defaultContactInfo);
  
  useEffect(() => {
    // In a real implementation, you would fetch this from backend
    // const fetchContactInfo = async () => {
    //   try {
    //     const response = await api.getContactInfo();
    //     setContactInfo(response);
    //     setEditedInfo(response);
    //   } catch (error) {
    //     console.error('Failed to fetch contact info:', error);
    //   }
    // };
    
    // fetchContactInfo();
    
    // Get from localStorage for demo purposes
    const savedInfo = localStorage.getItem('contactInfo');
    if (savedInfo) {
      const parsedInfo = JSON.parse(savedInfo);
      setContactInfo(parsedInfo);
      setEditedInfo(parsedInfo);
    }
  }, []);
  
  const handleSaveChanges = () => {
    // In a real implementation, you would send to backend
    // const saveContactInfo = async () => {
    //   try {
    //     await api.updateContactInfo(editedInfo);
    //     setContactInfo(editedInfo);
    //     setIsEditing(false);
    //   } catch (error) {
    //     console.error('Failed to update contact info:', error);
    //   }
    // };
    
    // saveContactInfo();
    
    // Save to localStorage for demo purposes
    localStorage.setItem('contactInfo', JSON.stringify(editedInfo));
    setContactInfo(editedInfo);
    setIsEditing(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const isAdmin = user?.username === 'admin'; // In a real app, check proper admin role
  
  return (
    <div className="bg-white rounded-xl shadow-md p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('technicalConsultation')}
        </h1>
        {isAdmin && (
          <div>
            {isEditing ? (
              <div className="space-x-2">
                <button 
                  onClick={handleSaveChanges}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                >
                  {t('save')}
                </button>
                <button 
                  onClick={() => {
                    setIsEditing(false);
                    setEditedInfo(contactInfo);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  {t('cancel')}
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                {t('edit')}
              </button>
            )}
          </div>
        )}
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-medium text-gray-800 mb-3">
          {t('howWeCanHelp')}
        </h2>
        <ul className="list-disc pl-5 space-y-2 text-gray-700">
          <li>{t('helpRegistration')}</li>
          <li>{t('helpBorderCrossing')}</li>
          <li>{t('helpDocumentation')}</li>
          <li>{t('helpLegalRequirements')}</li>
          <li>{t('helpTechnicalIssues')}</li>
        </ul>
      </div>
      
      <div className="bg-gray-50 p-6 rounded-lg">
        <h2 className="text-xl font-medium text-gray-800 mb-4">
          {t('contactUs')}
        </h2>
        
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('phone')}
              </label>
              <input
                type="text"
                name="phone"
                value={editedInfo.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email')}
              </label>
              <input
                type="email"
                name="email"
                value={editedInfo.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telegram
              </label>
              <input
                type="text"
                name="telegram"
                value={editedInfo.telegram}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp
              </label>
              <input
                type="text"
                name="whatsapp"
                value={editedInfo.whatsapp}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Viber
              </label>
              <input
                type="text"
                name="viber"
                value={editedInfo.viber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <Phone className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">{t('phone')}</h3>
                <a href={`tel:${contactInfo.phone}`} className="text-lg text-gray-900 hover:text-indigo-600">
                  {contactInfo.phone}
                </a>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <Mail className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">{t('email')}</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-lg text-gray-900 hover:text-indigo-600">
                  {contactInfo.email}
                </a>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-500 mb-3">{t('messagingApps')}</h3>
              <div className="flex space-x-4">
                <a 
                  href={contactInfo.telegram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-blue-500 text-white p-3 rounded-full hover:bg-blue-600"
                  title="Telegram"
                >
                  <Send className="h-6 w-6" />
                </a>
                <a 
                  href={contactInfo.whatsapp} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white p-3 rounded-full hover:bg-green-600"
                  title="WhatsApp"
                >
                  <MessageCircle className="h-6 w-6" />
                </a>
                <a 
                  href={contactInfo.viber} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-purple-500 text-white p-3 rounded-full hover:bg-purple-600"
                  title="Viber"
                >
                  <PhoneCall className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationPage;